import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { adminApi, errMsg, fmtDate, inr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Btn, PageHead, Panel, inputCls } from "@/admin/ui";

const ADDR = [["label", "Label"], ["name", "Name"], ["phone", "Phone"], ["line1", "Address line 1"], ["line2", "Address line 2"], ["city", "City"], ["state", "State"], ["postal_code", "PIN code"], ["country", "Country"]];

function CustomerDialog({ uid, onClose, onChanged }) {
  const [c, setC] = useState(null);
  const [f, setF] = useState(null);
  const [pw, setPw] = useState("");
  const load = useCallback(() => adminApi.get(`/customers/${uid}`).then((r) => { setC(r.data); setF({ name: r.data.name, phone: r.data.phone, addresses: r.data.addresses }); }), [uid]);
  useEffect(() => {
    load();
  }, [load]);
  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      await load();
      onChanged();
      return true;
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const setAddr = (i, k, v) => setF({ ...f, addresses: f.addresses.map((a, n) => (n === i ? { ...a, [k]: v } : a)) });
  if (!c) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="customer-dialog">
        <DialogHeader><DialogTitle className="font-head text-2xl">{c.email} {c.disabled && <span className="text-xs bg-zinc-800 text-white px-2 py-0.5 align-middle">Disabled</span>}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-2">
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" className={inputCls} data-testid="customer-edit-name" />
          <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone" className={inputCls} data-testid="customer-edit-phone" />
        </div>
        <p className="label-caps text-[10px] text-zinc-500 mt-2">Saved addresses</p>
        {f.addresses.map((a, i) => (
          <div key={i} className="grid grid-cols-3 gap-2 border border-zinc-200 p-3" data-testid={`customer-address-${i}`}>
            {ADDR.map(([k, l]) => <input key={k} value={a[k] || ""} placeholder={l} onChange={(e) => setAddr(i, k, e.target.value)} className={`${inputCls} ${k === "line1" || k === "line2" ? "col-span-3" : ""}`} data-testid={`customer-address-${i}-${k}`} />)}
            <button onClick={() => setF({ ...f, addresses: f.addresses.filter((_, n) => n !== i) })} className="text-xs text-red underline text-left" data-testid={`customer-address-remove-${i}`}>Remove address</button>
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          <Btn variant="outline" className="h-9 px-3" onClick={() => setF({ ...f, addresses: [...f.addresses, { label: "", name: f.name, phone: f.phone, line1: "", line2: "", city: "", state: "", postal_code: "", country: "India", default: !f.addresses.length }] })} data-testid="customer-address-add">Add address</Btn>
          <Btn variant="red" className="h-9 px-4 ml-auto" onClick={() => run(() => adminApi.put(`/customers/${uid}`, f), "Customer saved")} data-testid="customer-save">Save changes</Btn>
        </div>
        <div className="flex flex-wrap gap-2 border-t border-zinc-200 pt-4">
          <Btn variant="outline" className="h-9 px-3" onClick={() => run(() => adminApi.post(`/customers/${uid}/password-reset`), `Password reset email sent to ${c.email}`)} data-testid="customer-reset-password">Send password reset email</Btn>
          <Btn variant="outline" className="h-9 px-3" onClick={() => run(() => adminApi.post(`/customers/${uid}/disable`, { disabled: !c.disabled }), c.disabled ? "Account enabled" : "Account disabled")} data-testid="customer-toggle-disable">{c.disabled ? "Enable account" : "Disable account"}</Btn>
          <Btn variant="ghost" className="h-9 px-3 text-red" onClick={() => { if (window.confirm(`Delete the account profile for ${c.email}? Their orders are kept.`)) run(() => adminApi.delete(`/customers/${uid}`), "Customer deleted").then(onClose); }} data-testid="customer-delete">Delete account</Btn>
        </div>
        <div className="border-t border-zinc-200 pt-4 space-y-2" data-testid="customer-set-password">
          <p className="label-caps text-[10px] text-zinc-500">Set a new password</p>
          <div className="flex flex-wrap gap-2">
            <input type="text" autoComplete="off" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" className={`${inputCls} max-w-xs`} data-testid="customer-new-password" />
            <Btn variant="outline" className="h-9 px-3" disabled={!pw} onClick={() => run(() => adminApi.post(`/customers/${uid}/password`, { password: pw }), "Password changed").then((ok) => ok && setPw(""))} data-testid="customer-set-password-btn">Change password</Btn>
          </div>
          <p className="text-xs text-zinc-500">Passwords are stored securely and can't be viewed. Changing it signs the customer out on all devices. Share the new password with them yourself.</p>
        </div>
        <p className="label-caps text-[10px] text-zinc-500 mt-2">Orders ({c.orders.length})</p>
        <ul className="text-sm divide-y divide-zinc-100" data-testid="customer-orders">
          {c.orders.map((o) => <li key={o.id} className="py-2 flex justify-between gap-3"><span><b>{o.order_number}</b> · {fmtDate(o.created_at)}</span><span>{o.payment_status} · {o.stage_name} · {inr(o.total)}</span></li>)}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

export default function Customers() {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => adminApi.get("/customers", { params: { q } }).then((r) => setData(r.data)), [q]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  return (
    <div>
      <PageHead title="Customers" desc="Customers who created an account. Their orders include everything placed with their email address." />
      <div className="relative max-w-sm mb-4"><Search className="w-4 h-4 absolute left-3 top-3 text-zinc-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or phone" className={`${inputCls} pl-9`} data-testid="customers-search" /></div>
      <Panel className="p-0 sm:p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="customers-table">
          <thead><tr className="text-left label-caps text-[10px] text-zinc-500 border-b border-zinc-200">{["Customer", "Phone", "Addresses", "Orders", "Joined"].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}</tr></thead>
          <tbody>
            {data?.items.map((c) => (
              <tr key={c.user_id} onClick={() => setOpen(c.user_id)} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`customer-row-${c.email}`}>
                <td className="px-4 py-3">{c.name || "—"}<div className="text-xs text-zinc-500">{c.email}{c.disabled && " · disabled"}</div></td>
                <td className="px-4 py-3">{c.phone}</td><td className="px-4 py-3">{c.addresses.length}</td><td className="px-4 py-3">{c.order_count}</td><td className="px-4 py-3">{fmtDate(c.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.items.length === 0 && <p className="p-8 text-sm text-zinc-500" data-testid="customers-empty">No customer accounts yet.</p>}
      </Panel>
      {open && <CustomerDialog uid={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
